// @flow
import OrderProcessingApi from "../api/OrderProcessingApi";
import type {ApplicationState} from "../reducers";
import type {PaymentMethodResponse} from "../api/PaymentApi";
import PaymentApi from "../api/PaymentApi";
import {toast} from "react-toastify";
import i18n from "../i18next";
import CouponApi from "../api/CouponApi";
import {couponNotValid, getSlyCustomer, getSpsDeposit} from "./coupon";
import {
    allFromCartRemoved,
    fromCartRemoved,
    paymentMethodsFailureGotten,
    paymentMethodsRequestGotten,
    paymentMethodsSuccessGotten, paypalClientIdSet,
    quantitySet,
    toCartAdded
} from "../reducers/cartReducer";
import {
    cartAndCouponSuccessChecked,
    cartFailureChecked,
    cartRequestChecked,
    cartSuccessChecked, orderDetailsCleared,
} from "../reducers/orderReducer";
import type {Coupon} from "../models/Coupon";
import {CouponState} from "../models/Coupon";

export const CHECK_CART_REQUEST = "CHECK_CART_REQUEST";
export const NO_PAYPAL_CONFIGURED = null;
export const SLY_CUSTOMER = "SLY_CUSTOMER";

export const addToCart = (product, shippingMethod) => {
	return toCartAdded({
		product,
		shippingMethod
	});
};

export const loadPaymentMethods = () => {
	return (dispatch, getState: () => ApplicationState) => {
		dispatch(paymentMethodsRequestGotten());
		const paymentApi = new PaymentApi();

		paymentApi.getPaymentMethods(getState().config.dealerId)
			.then((res1: PaymentMethodResponse) => {
				paymentApi.getPaypalClientId(getState().config.dealerId)
					.then(res2 => {
						dispatch(paymentMethodsSuccessGotten({
							paymentMethods: res1.paymentMethods,
							clientId: res2.clientId || NO_PAYPAL_CONFIGURED,
						}));
					}).catch(_e => {
                        dispatch(paymentMethodsSuccessGotten({
                            paymentMethods: res1.paymentMethods,
                            clientId: NO_PAYPAL_CONFIGURED,
                        }));
    				});
			})
			.catch(() => {
				dispatch(paymentMethodsFailureGotten());
			});
	};
};

export const loadPaypalClientId = (dealerId: string) => {
    return (dispatch) => {
        const paymentApi = new PaymentApi();
        paymentApi.getPaypalClientId(dealerId)
            .then((res) => {
                dispatch(paypalClientIdSet(res.clientId || NO_PAYPAL_CONFIGURED));
            })
            .catch((err) => {
                console.error("Error getting paypal client id: ", err);
            })
    }
}

export const checkCart = () => {

	return (dispatch, getState: () => ApplicationState) => {
        if(!getState().cart.items || getState().cart.items.length === 0) {
            dispatch(orderDetailsCleared())
            return;
        }

        dispatch(cartRequestChecked());

        const orderApi = new OrderProcessingApi();
		const pickupLocation = getState().location.pickupLocation ? {index: getState().location.pickupLocation.cfgKey} : null;
		const couponApi = new CouponApi();
		const myCoupon = (getState().coupon.coupon && getState().coupon.coupon.coupon) ? getState().coupon.coupon.coupon : null;
		const couponId = (myCoupon && myCoupon.couponNo && myCoupon.couponNo.index) ? myCoupon.couponNo.index : null;
		const couponCode = (myCoupon && myCoupon.code) ? myCoupon.code : undefined;
		const dealerId = getState().config.dealerId;
		const slyToken = getState().coupon.slyToken;
		const spsCustomer = getState().coupon.sps;

        if(slyToken) {
            dispatch(getSlyCustomer(dealerId, slyToken));
        }

        if(spsCustomer) {
            dispatch(getSpsDeposit());
        }

		const cartValidationRequest = {
			dealerNo: {
				index: getState().config.dealerId
			},
			assembler: pickupLocation,
			items: getState().cart.items.map(item => {
				return {
					...item,
					product: {
						dealerId: getState().config.dealerId,
						productNo: item.product.articleNumber,
						purchaseOrderNo: item.product.id
					},
					shippingMethod: {
						code: item.shippingMethod
					},
					amount: {
						amount: item.amount,
						unit: {
							code: "piece"
						}
					}
				};
			}),
			couponCode,
			paymentProvider: {
				code: "paypal-express"
			},
		};

        const setCouponAndReduceOrderAmount = (couponResponse: Coupon, calcOrderResponse) => {
			dispatch(cartAndCouponSuccessChecked({
				orderItemsPriceSum: calcOrderResponse.orderItems.orderItemsPriceSum,
				orderItemsWithFullPriceSum: calcOrderResponse.orderItems.orderItemsWithFullPriceSum,
				shippingCosts: calcOrderResponse.orderItems.shippingCosts,
				orderItemsPriceSumWithShipping: {
					...calcOrderResponse.orderItems.orderItemsPriceSumWithShipping,
					currency: calcOrderResponse.orderItems.orderItemsPriceSumWithShipping.currency.code
				},
				totalBillingAmount: {
					...calcOrderResponse.orderItems.orderItemsPriceSumWithShipping,
					cost: Math.max(0, calcOrderResponse.orderItems.orderItemsPriceSumWithShipping.cost - couponResponse.coupon.eligibleAmount.value)
				},
				coupon: couponResponse,
			}));
		};

		orderApi.calcOrder(cartValidationRequest)
			.then(orderResponse => {
				if (couponId) {
					const totalOrderValue = {
						cost: orderResponse.orderItems.orderItemsPriceSum.cost,
						currency: orderResponse.orderItems.orderItemsPriceSum.currency.code
					};

					const totalFullPriceOrderValue = orderResponse.orderItems.orderItemsWithFullPriceSum ? {
						cost: orderResponse.orderItems.orderItemsWithFullPriceSum.cost,
						currency: orderResponse.orderItems.orderItemsWithFullPriceSum.currency ? orderResponse.orderItems.orderItemsWithFullPriceSum.currency.code : null
					} : null;

					const shippingCostValue = {
						cost: orderResponse.orderItems.shippingCosts.cost,
						currency: orderResponse.orderItems.shippingCosts.currency.code
					};

					couponApi
						.checkCouponId(
							couponId,
							totalOrderValue, totalFullPriceOrderValue, shippingCostValue
						)
						.then(couponResponse => setCouponAndReduceOrderAmount(couponResponse, orderResponse))
						.catch(e => {
							if (e.response && e.response.data && e.response.data.couponState && CouponState.isBelowRequiredAmount(e.response.data.couponState)) {
								setCouponAndReduceOrderAmount(e.response.data, orderResponse);
							} else {
								dispatch(couponNotValid(couponId, e, "error"));
							}
						});
				} else {
					dispatch(cartSuccessChecked({
						totalBillingAmount: orderResponse.orderItems.orderItemsPriceSumWithShipping,
						orderItemsWithFullPriceSum: orderResponse.orderItems.orderItemsWithFullPriceSum,
						shippingCosts: orderResponse.orderItems.shippingCosts,
						orderItemsPriceSumWithShipping: orderResponse.orderItems.orderItemsPriceSumWithShipping,
						orderItemsPriceSum: orderResponse.orderItems.orderItemsPriceSum,
					}));
				}
			})
			.catch(error => {
				console.error("Order calculation failed:", error);
				dispatch(cartFailureChecked());
			});
	};
};

export const removeFromCart = product => {
	toast(i18n.t("productInteractions.remove"));
	return fromCartRemoved(product);
};

export const makeCartEmpty = () => {
	return (dispatch, getState: () => ApplicationState) => {
		if (getState().cart.items.length > 0) {
			toast(i18n.t("productInteractions.clear"));
			dispatch(allFromCartRemoved);
		}
	};
};

export const setQuantity = (id, amount) => quantitySet({
	id: id,
	amount: amount
});
